/* Share-grid builder. Output is:

   1. Header — "PALINDRAGON ID"
   2. Variable-height block of emoji rows, each preserving the solution's
      spacing and punctuation:
        - submitted guesses → 🟩/🟨/⬛ feedback
        - hint-paid slots   → 🟧 (when the player bought the hint, those
                               two slots are visually charged to the cost)
        - spaces-paid slots → 🟦 (when the player bought the spaces
                               reveal, those three slots are visually
                               charged to the cost)
      Before the first move, the block is a single empty-solution row of
      ⬜ (the shape of the puzzle, no progress). Once any slot has been
      used (a submission or a reveal purchase), the block grows to cover
      every used slot and stops there — no trailing ⬜ rows.
   3. Footer — "X/Y • SPACES PART • HINT PART".
      X is total slots used (submissions + reveal costs); Y is maxGuesses.
      Each PART is either "NO SPACES"/"SPACES AT M" or "NO HINT"/"HINT
      AT N", where M and N are 1-based slot numbers identifying where
      each reveal landed.
   4. Optional Hard-Mode line — "🐲 HARD MODE SOLVE! 🐲" on its own line
      below the footer when the player won without buying either reveal.
      Loss and "won with help" stay legible only via the emoji rows.

   Reveal-paid rows sit at the slots where they were purchased (mirroring
   the board layout): submitted rows before a purchase keep their slots,
   later submissions get pushed down by the cost. Caller is responsible
   for passing the correct final-layout slot indices for hintPurchasedAt
   and spacesPurchasedAt — the function does not reorder reveals.

   Submitted-empty cells share the gray emoji with not-in-solution cells:
   from a viewer's standpoint both are "no info revealed", and conflating
   them keeps the share grid in Wordle's two-state vocabulary (hit / miss).

   `buildShareGrid` returns a structured representation (cells, separators,
   header, footer, hard-mode flag) used by both `buildShareText` (which
   collapses each row to an emoji string) and `shareImage.ts` (which paints
   the same rows as colored squares onto a canvas for clipboard-as-PNG). */

import type { Mark } from "./scoring";

/** Visual state of one filled square in the share grid. */
export type SquareMark =
  | "green"        // 🟩 — correct letter, correct position
  | "yellow"       // 🟨 — correct letter, mirror position
  | "gray"         // ⬛ — incorrect letter or empty submission
  | "placeholder"  // ⬜ — slot not yet used (only in the no-progress preview)
  | "hint"         // 🟧 — hint-paid row
  | "spaces";      // 🟦 — spaces-paid row

/** A single positional unit in a share grid row. Letter slots become
   `square` cells; the solution's separators ride along verbatim so the
   image and the text both preserve the puzzle's word/phrase shape. */
export type GridCell =
  | { kind: "square"; mark: SquareMark }
  | { kind: "space" }
  | { kind: "punct"; ch: string };

export type ShareGrid = {
  header: string;
  rows: GridCell[][];
  footer: string;
  hardMode: boolean;
};

const SQUARE_EMOJI: Record<SquareMark, string> = {
  green: "🟩",
  yellow: "🟨",
  gray: "⬛️",
  placeholder: "⬜️",
  hint: "🟧",
  spaces: "🟦",
};

const LETTER_RE = /[a-z]/i;

/** Turn a sentence + a per-letter mark function into a row of GridCells.
   The function is called once per letter position, in order. */
function rowFromSentence(
  sentence: string,
  markAt: (letterIndex: number) => SquareMark,
): GridCell[] {
  const cells: GridCell[] = [];
  let li = 0;
  for (const ch of sentence) {
    if (LETTER_RE.test(ch)) {
      cells.push({ kind: "square", mark: markAt(li++) });
    } else if (ch === " ") {
      cells.push({ kind: "space" });
    } else {
      cells.push({ kind: "punct", ch });
    }
  }
  return cells;
}

/** Map a scoring `Mark` to its share-grid `SquareMark`. The two distinct
   "no info" marks (`gray` for incorrect letters, `empty` for unfilled
   positions in a partial submission) collapse to a single `gray` square
   so the share grid stays in the two-state hit/miss vocabulary. */
function markToSquare(m: Mark): SquareMark {
  return m === "green" ? "green" : m === "yellow" ? "yellow" : "gray";
}

export function buildShareGrid(
  marks: readonly (readonly Mark[])[],
  puzzleId: string,
  sentence: string,
  maxGuesses: number,
  hintPurchasedAt: number | null,
  hintCost: number,
  spacesPurchasedAt: number | null,
  spacesCost: number,
  won: boolean,
): ShareGrid {
  const header = `PALINDRAGON ${puzzleId}`;

  // Slot-driven walk: each slot gets either a reveal row (if it falls
  // inside one of the reveal blocks), the next pending guess row, or
  // remains a placeholder. Mirrors the board / playthrough walk.
  const placeholderRow = (): GridCell[] =>
    rowFromSentence(sentence, () => "placeholder");
  const hintRow = (): GridCell[] => rowFromSentence(sentence, () => "hint");
  const spacesRow = (): GridCell[] => rowFromSentence(sentence, () => "spaces");

  const rows: GridCell[][] = new Array(maxGuesses);
  const used: boolean[] = new Array(maxGuesses).fill(false);
  for (let i = 0; i < maxGuesses; i++) rows[i] = placeholderRow();

  let g = 0;
  for (let slot = 0; slot < maxGuesses; slot++) {
    if (
      hintPurchasedAt != null &&
      slot >= hintPurchasedAt &&
      slot < hintPurchasedAt + hintCost
    ) {
      rows[slot] = hintRow();
      used[slot] = true;
    } else if (
      spacesPurchasedAt != null &&
      slot >= spacesPurchasedAt &&
      slot < spacesPurchasedAt + spacesCost
    ) {
      rows[slot] = spacesRow();
      used[slot] = true;
    } else if (g < marks.length) {
      const m = marks[g]!;
      rows[slot] = rowFromSentence(sentence, (i) => markToSquare(m[i]!));
      used[slot] = true;
      g++;
    }
  }

  let lastUsed = -1;
  for (let i = used.length - 1; i >= 0; i--) {
    if (used[i]) {
      lastUsed = i;
      break;
    }
  }
  const visibleRows: GridCell[][] =
    lastUsed >= 0 ? rows.slice(0, lastUsed + 1) : [placeholderRow()];

  const guessesUsed =
    marks.length +
    (hintPurchasedAt != null ? hintCost : 0) +
    (spacesPurchasedAt != null ? spacesCost : 0);
  const spacesPart =
    spacesPurchasedAt != null
      ? `SPACES AT ${spacesPurchasedAt + 1}`
      : "NO SPACES";
  const hintPart =
    hintPurchasedAt != null ? `HINT AT ${hintPurchasedAt + 1}` : "NO HINT";
  const footer = `${guessesUsed}/${maxGuesses} • ${spacesPart} • ${hintPart}`;
  const hardMode =
    won && hintPurchasedAt == null && spacesPurchasedAt == null;

  return { header, rows: visibleRows, footer, hardMode };
}

/** Render one structured row to its emoji-text equivalent. */
function rowToText(row: GridCell[]): string {
  let out = "";
  for (const c of row) {
    if (c.kind === "square") out += SQUARE_EMOJI[c.mark];
    else if (c.kind === "space") out += " ";
    else out += c.ch;
  }
  return out;
}

export function buildShareText(
  marks: readonly (readonly Mark[])[],
  puzzleId: string,
  sentence: string,
  maxGuesses: number,
  hintPurchasedAt: number | null,
  hintCost: number,
  spacesPurchasedAt: number | null,
  spacesCost: number,
  won: boolean,
): string {
  const grid = buildShareGrid(
    marks,
    puzzleId,
    sentence,
    maxGuesses,
    hintPurchasedAt,
    hintCost,
    spacesPurchasedAt,
    spacesCost,
    won,
  );
  const lines = [grid.header, ...grid.rows.map(rowToText), grid.footer];
  if (grid.hardMode) lines.push("🐲 HARD MODE SOLVE! 🐲");
  return lines.join("\n");
}
